import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import L from 'leaflet';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const pinIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const roundCoord = (value: number) => Math.round(value * 1e6) / 1e6;

const parseCoordinate = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const isValidPosition = (latitude: number | null, longitude: number | null) =>
  latitude !== null &&
  longitude !== null &&
  latitude >= -90 &&
  latitude <= 90 &&
  longitude >= -180 &&
  longitude <= 180;

interface MapControllerProps {
  latitude: number | null;
  longitude: number | null;
  onChange: (latitude: number, longitude: number) => void;
}

const MapController = ({ latitude, longitude, onChange }: MapControllerProps) => {
  const map = useMap();
  const mapUpdateRef = useRef<{ lat: number; lng: number } | null>(null);

  const applyMapPosition = (lat: number, lng: number) => {
    const nextLatitude = roundCoord(lat);
    const nextLongitude = roundCoord(lng);
    mapUpdateRef.current = { lat: nextLatitude, lng: nextLongitude };
    onChange(nextLatitude, nextLongitude);
  };

  useMapEvents({
    click(event) {
      applyMapPosition(event.latlng.lat, event.latlng.lng);
    },
  });

  useEffect(() => {
    map.getContainer().style.cursor = 'crosshair';
    const timer = window.setTimeout(() => map.invalidateSize(), 0);
    return () => window.clearTimeout(timer);
  }, [map]);

  useEffect(() => {
    if (latitude === null || longitude === null) {
      return;
    }

    const fromMap = mapUpdateRef.current;
    const cameFromMap =
      fromMap !== null &&
      Math.abs(fromMap.lat - latitude) < 1e-6 &&
      Math.abs(fromMap.lng - longitude) < 1e-6;

    if (cameFromMap && map.getZoom() >= 8) {
      return;
    }

    const center = map.getCenter();
    if (
      Math.abs(center.lat - latitude) < 1e-5 &&
      Math.abs(center.lng - longitude) < 1e-5 &&
      map.getZoom() >= 8
    ) {
      return;
    }

    const zoom = map.getZoom() < 8 ? 13 : map.getZoom();
    map.setView([latitude, longitude], zoom);
  }, [latitude, longitude, map]);

  if (latitude === null || longitude === null) {
    return null;
  }

  return (
    <Marker
      position={[latitude, longitude]}
      icon={pinIcon}
      draggable
      eventHandlers={{
        dragend: (event) => {
          const marker = event.target as L.Marker;
          const { lat, lng } = marker.getLatLng();
          applyMapPosition(lat, lng);
        },
      }}
    />
  );
};

const FitMap = ({ latitude, longitude }: { latitude: number; longitude: number }) => {
  const map = useMap();

  useEffect(() => {
    const fit = () => {
      map.invalidateSize();
      map.setView([latitude, longitude], 12, { animate: false });
    };
    const timer = window.setTimeout(fit, 0);
    return () => window.clearTimeout(timer);
  }, [map, latitude, longitude]);

  return null;
};

interface LocationPreviewProps {
  latitude: number | string | null | undefined;
  longitude: number | string | null | undefined;
}

export const LocationPreview = ({ latitude, longitude }: LocationPreviewProps) => {
  const parsedLatitude = parseCoordinate(latitude);
  const parsedLongitude = parseCoordinate(longitude);
  if (!isValidPosition(parsedLatitude, parsedLongitude)) {
    return null;
  }

  const position: [number, number] = [parsedLatitude as number, parsedLongitude as number];

  return (
    <Box
      sx={{
        width: 240,
        height: 148,
        flexShrink: 0,
        borderRadius: 1,
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        position: 'relative',
        zIndex: 0,
      }}
    >
      <MapContainer
        center={position}
        zoom={12}
        dragging={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        zoomControl={false}
        attributionControl={false}
        keyboard={false}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' />
        <FitMap latitude={position[0]} longitude={position[1]} />
        <Marker position={position} icon={pinIcon} interactive={false} />
      </MapContainer>
      <Typography
        variant='caption'
        sx={{
          position: 'absolute',
          right: 4,
          bottom: 4,
          zIndex: 1,
          px: 0.5,
          borderRadius: 0.5,
          bgcolor: 'rgba(255,255,255,0.88)',
          color: 'text.secondary',
          fontSize: 10,
          lineHeight: 1.4,
        }}
      >
        © OpenStreetMap
      </Typography>
    </Box>
  );
};

interface LocationPickerProps {
  latitude: number | string | null | undefined;
  longitude: number | string | null | undefined;
  onChange: (latitude: number, longitude: number) => void;
}

const LocationPicker = ({ latitude, longitude, onChange }: LocationPickerProps) => {
  const parsedLatitude = parseCoordinate(latitude);
  const parsedLongitude = parseCoordinate(longitude);
  const position = isValidPosition(parsedLatitude, parsedLongitude)
    ? { latitude: parsedLatitude as number, longitude: parsedLongitude as number }
    : null;

  const [initialView] = useState(() =>
    position
      ? {
          center: [position.latitude, position.longitude] as [number, number],
          zoom: 13,
        }
      : {
          center: [20, 0] as [number, number],
          zoom: 2,
        },
  );

  return (
    <Box>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
        Click the map or drag the pin to set the location. You can also type the coordinates above.
      </Typography>
      <Box
        sx={{
          height: { xs: 280, sm: 360 },
          borderRadius: 1,
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
          position: 'relative',
          zIndex: 0,
        }}
      >
        <MapContainer
          center={initialView.center}
          zoom={initialView.zoom}
          scrollWheelZoom={false}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          />
          <MapController
            latitude={position?.latitude ?? null}
            longitude={position?.longitude ?? null}
            onChange={onChange}
          />
        </MapContainer>
      </Box>
    </Box>
  );
};

export default LocationPicker;
